import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import PrintDetails from 'src/components/design-system/PrintDetails';
import useReceiptDetails from 'src/hooks/useReceiptDetails';
import usePoHistoryApi from 'src/services/usePoHistoryApi';
import { ReceiptLineItem } from 'src/services/useReceiptLineItemsApi';
import PDF from './PrintPDF';

export interface LineItems {
  data: ReceiptLineItem[];
  total: number;
}
interface IReceiptCancelPoWizardProps {}

const PrintWizard: FC<IReceiptCancelPoWizardProps> = () => {
  const [searchParams] = useSearchParams();
  const poId = searchParams.get('poid') || '';
  const { poHistory, getPoHistory, isLoading: isPoHistoryLoading } = usePoHistoryApi(poId, false);
  const recid = searchParams.get('recid');
  const { receiptData, receiptLineItems } = useReceiptDetails();
  const [isPrintLoading, setIsPrintLoading] = useState<boolean>(false);

  const {
    poNum,
    receiptStatus: status,
    creationDate: receiptDate,
    buyerDetails,
    supplierDetails,
    receiptNotes,
    deliveryDateTime,
    deliveryDriverName,
    deliveryCompanyName,
    attachments,
  } = receiptData;

  let requiredLineItems = useMemo(
    () =>
      receiptLineItems.map((lineObj) => {
        const { poQty, currentDocQty, previousDocQty } = lineObj;
        return {
          ...lineObj,
          varianceQty: Number(poQty) - (Number(currentDocQty) + Number(previousDocQty)),
        };
      }),
    [receiptLineItems]
  );

  const poCol1Values = useMemo(() => [poNum, status, receiptDate], [poNum, receiptDate, status]);
  const poCol2Values = useMemo(
    () => [deliveryDateTime, deliveryDriverName, deliveryCompanyName],
    [deliveryCompanyName, deliveryDateTime, deliveryDriverName]
  );

  const [state, setState] = useState({
    history: false,
    notes: false,
    attachments: false,
  });

  useEffect(() => {
    if (state?.history) {
      getPoHistory();
    }
  }, [getPoHistory, state?.history]);

  const pdf = useMemo(
    () => (
      <PDF
        receiptId={recid || ''}
        buyerDetails={buyerDetails}
        supplierDetails={supplierDetails}
        receiptLineItems={requiredLineItems}
        po1={poCol1Values}
        po2={poCol2Values}
        notes={receiptNotes}
        history={
          poHistory?.map((history) => ({
            ...history,
            document: history.document?.split(':')?.[0],
          })) || []
        }
        showHistory={state.history}
        showNotes={state.notes}
        showAttachment={state?.attachments}
        attachDocuments={attachments?.map((attachment) => attachment?.signedUrl) || []}
        isPoHistoryLoading={isPoHistoryLoading}
        setIsPrintLoading={setIsPrintLoading}
      />
    ),
    [
      attachments,
      buyerDetails,
      isPoHistoryLoading,
      poCol1Values,
      poCol2Values,
      poHistory,
      receiptNotes,
      recid,
      requiredLineItems,
      state?.attachments,
      state.history,
      state.notes,
      supplierDetails,
    ]
  );

  return (
    <PrintDetails
      pdf={pdf}
      setState={setState}
      state={state}
      documentNumber={receiptData?.receivingReceiptId?.toString() || ''}
      title="Receipt"
      isPoHistoryLoading={isPoHistoryLoading}
      isPrintLoading={isPrintLoading}
      setIsPrintLoading={setIsPrintLoading}
    />
  );
};

export default PrintWizard;
